import { useEffect, type RefObject } from 'react'

/**
 * Reveal-on-scroll, built to Quil's contract rather than to the kit's.
 *
 * The kit (`app-platform/blocks/cupertino-kit.html`) reveals on an `IntersectionObserver` and
 * nothing else. That is a straight collision with `build/DESIGN.md` §3 "reveal robustness", which
 * is a **hard implementation rule here, not a preference**, and which exists because three separate
 * bugs of exactly this shape hid real content while the v3 preview was being built:
 *
 *   · reveal-on-scroll starts content at `opacity: 0`, so **visibility depends on something
 *     running**. If it does not run, the page is blank and nothing reports it.
 *   · so: several independent signals, **plus a failsafe that reveals everything regardless**
 *   · so: the revealed state sets its own final opacity, never an animation's fill mode
 *   · so: nodes created after the pass are shown immediately, not queued for a pass that is over
 *
 * Four things make that true here:
 *
 * 1. **The hidden state is armed by script.** `armedClass` is added by this hook, so the CSS that
 *    hides anything only exists once the code that un-hides it is demonstrably running. With no
 *    JS at all, the page is simply a page. This is the single most important line in the file.
 * 2. **Six signals**, any one of which is enough: a synchronous pass at mount, the next frame,
 *    `load`, `document.fonts.ready`, any scroll, and the observer.
 * 3. **A failsafe** at `FAILSAFE_MS` adds `allInClass`, which reveals every `[data-reveal]` under
 *    the root through the CSS rather than node by node — so anything added later is covered too,
 *    with no MutationObserver to go wrong.
 * 4. **Reduced motion swaps, never strips** (§3 motion): the media query in the stylesheet puts
 *    everything in its final position at full opacity. Nothing here needs to know about it.
 */

/** Long enough that it never pre-empts a working reveal; short enough that nobody reads a blank. */
const FAILSAFE_MS = 2600

export function useReveal(
  rootRef: RefObject<HTMLElement | null>,
  armedClass: string,
  allInClass: string,
): void {
  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    // Signal 0, and the reason the other five are allowed to fail: nothing is hidden until this
    // line runs.
    root.classList.add(armedClass)

    const ac = new AbortController()
    const { signal } = ac
    const nodes = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]'))

    /** In view, or already scrolled past. Cheap, synchronous, and needs no observer. */
    const isUp = (n: HTMLElement) => {
      const r = n.getBoundingClientRect()
      return r.top < window.innerHeight * 0.92 && r.bottom > 0
    }

    const sweep = () => {
      for (const n of nodes) {
        if (!n.classList.contains('in') && isUp(n)) n.classList.add('in')
      }
    }

    // Signals 1-5. Each is independent; any one of them is enough on its own.
    sweep()
    const frame = requestAnimationFrame(sweep)
    window.addEventListener('load', sweep, { signal })
    window.addEventListener('scroll', sweep, { passive: true, signal })
    document.fonts?.ready.then(sweep).catch(() => {})

    let io: IntersectionObserver | null = null
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue
            entry.target.classList.add('in')
            io?.unobserve(entry.target)
          }
        },
        { threshold: 0.15 },
      )
      nodes.forEach((n) => io?.observe(n))
    }

    // The failsafe. Not a signal: it does not check anything, it just reveals.
    const backstop = window.setTimeout(() => root.classList.add(allInClass), FAILSAFE_MS)

    return () => {
      ac.abort()
      cancelAnimationFrame(frame)
      window.clearTimeout(backstop)
      io?.disconnect()
      root.classList.remove(armedClass, allInClass)
    }
  }, [rootRef, armedClass, allInClass])
}
